/**
 * OwnerPanels
 *
 * Agrupa en un solo archivo todos los paneles laterales relacionados
 * con dueños: visualización (read-only) y creación/edición.
 *
 * Uso en Dashboard:
 *   const ownerPanels = useOwnerPanels();
 *   ...
 *   ownerPanels.openDetail(owner)   // botón "Ver"
 *   ownerPanels.openEdit(owner)     // botón "Editar"
 *   ownerPanels.openCreate()        // botón "Nuevo Dueño"
 *   ...
 *   <OwnerPanels hook={ownerPanels} allCompanies={allCompanies} onSave={loadData} />
 */

import { useState } from "react";
import { type User } from "@/services/usersService";
import { type Company } from "@/services/companiesService";
import OwnerDetailModal from "@/components/OwnerDetailModal";
import OwnerFormSheet from "@/components/OwnerFormSheet";

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useOwnerPanels() {
  // Panel de detalle (solo lectura)
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [detailOwner, setDetailOwner] = useState<User | null>(null);

  // Panel de formulario (crear / editar)
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formOwner, setFormOwner] = useState<User | null>(null);

  const openDetail = (owner: User) => {
    setDetailOwner(owner);
    setIsDetailOpen(true);
  };

  const openEdit = (owner: User) => {
    setFormOwner(owner);
    setIsFormOpen(true);
  };

  const openCreate = () => {
    setFormOwner(null);
    setIsFormOpen(true);
  };

  return {
    // Estado interno — usado por <OwnerPanels />
    _state: { isDetailOpen, detailOwner, isFormOpen, formOwner },
    _setters: { setIsDetailOpen, setIsFormOpen },
    // Handlers públicos — usados por Dashboard
    openDetail,
    openEdit,
    openCreate,
  };
}

// ─── Componente ──────────────────────────────────────────────────────────────

interface OwnerPanelsProps {
  hook: ReturnType<typeof useOwnerPanels>;
  /** Lista de empresas para el selector en el formulario */
  allCompanies: Company[];
  /** Se llama tras crear / editar / cambiar estado para recargar datos */
  onSave: () => void;
}

export function OwnerPanels({ hook, allCompanies, onSave }: OwnerPanelsProps) {
  const { _state: s, _setters: set } = hook;

  return (
    <>
      {/* Panel de visualización */}
      <OwnerDetailModal
        isOpen={s.isDetailOpen}
        onClose={() => set.setIsDetailOpen(false)}
        owner={s.detailOwner}
      />

      {/* Panel de creación / edición */}
      <OwnerFormSheet
        isOpen={s.isFormOpen}
        onClose={() => set.setIsFormOpen(false)}
        owner={s.formOwner}
        companies={allCompanies}
        onSave={onSave}
      />
    </>
  );
}
